// ============================================================
// PREZENCE — Auth Context
// Manages user session, login, logout across the whole app
// ============================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true); // true while checking saved token

  // On app start — restore session from localStorage
  useEffect(() => {
    const token = localStorage.getItem('prezence_token');
    const saved  = localStorage.getItem('prezence_user');
    const doLogout = () => {
      localStorage.removeItem('prezence_token');
      localStorage.removeItem('prezence_user');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
    };
    if (token && saved) {
      try {
        setUser(JSON.parse(saved));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        api.get('/auth/me')
          .then(res => {
            setUser(res.data.user);
            localStorage.setItem('prezence_user', JSON.stringify(res.data.user));
          })
          .catch(() => doLogout())
          .finally(() => setLoading(false));
      } catch {
        doLogout();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('prezence_token', token);
    localStorage.setItem('prezence_user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('prezence_token');
    localStorage.removeItem('prezence_user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('prezence_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
